import React, { useState, useEffect } from 'react';
import { Shield, TrendingUp, TrendingDown, DollarSign, BarChart3, Lock, ChevronRight, AlertCircle, Target, Zap, CheckCircle, Activity, Users, FileText, ArrowRight } from 'lucide-react';

// Font styles configuration
const fontStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600&display=swap');
  
  @font-face {
    font-family: 'Engravers Gothic';
    src: url('/fonts/EngraversGothic-Bold.woff2') format('woff2');
    font-weight: 700;
    font-style: normal;
    font-display: swap;
  }
  
  @font-face {
    font-family: 'Source Serif Pro';
    src: url('/fonts/SourceSerifPro-Regular.woff2') format('woff2');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
  }
  
  @font-face {
    font-family: 'Untitled Sans';
    src: url('/fonts/UntitledSans-Regular.woff2') format('woff2');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
  }
`;

const App: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);

      // Update active section based on scroll position
      const sections = ['hero', 'why-needed', 'how-it-works', 'compliance', 'dashboard'];
      const scrollPosition = window.scrollY + 200;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element && scrollPosition >= element.offsetTop) {
          setActiveSection(section);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Animation helper for fade-up effect
  const fadeUpAnimation = (delay = 0) => ({
    animation: `fadeUp 0.6s ease-out ${delay}ms forwards`,
    opacity: 0
  });

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: fontStyles }} />
      <style>{`
        :root {
          --color-winter-white: #F9FDFE;
          --color-dark-blue: #051726;
          --color-navy-blue: #0E3D59;
          --color-dark-gray: #A6A5A4;
          --color-burgundy-brown: #A67655;
          --color-light-blue-gray: #E5F0F5;
        }
        
        @keyframes fadeUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        
        body {
          font-family: 'Source Serif Pro', Georgia, serif;
          color: var(--color-dark-gray);
          background-color: var(--color-winter-white);
        }
        
        h1, h2, h3, nav {
          font-family: 'Engravers Gothic', 'Arial Black', sans-serif;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        
        .logo-font {
          font-family: 'Playfair Display', 'Times New Roman', serif;
          font-weight: 600;
          line-height: 1.2;
        }
        
        .data-font {
          font-family: 'Untitled Sans', 'Segoe UI', Arial, sans-serif;
        }
        
        .cta-button {
          background-color: var(--color-burgundy-brown);
          color: var(--color-winter-white);
          font-family: 'Engravers Gothic', sans-serif;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          padding: 15px 40px;
          border-radius: 12px;
          font-size: 20px;
          transition: all 0.3s ease;
          box-shadow: 0 4px 12px rgba(166, 118, 85, 0.3);
        }
        
        .cta-button:hover {
          transform: scale(1.05);
          box-shadow: 0 6px 20px rgba(166, 118, 85, 0.4);
        }
        
        .gradient-bg {
          background: linear-gradient(135deg, var(--color-winter-white) 0%, var(--color-light-blue-gray) 100%);
        }
        
        .stripe-bg {
          background-image: repeating-linear-gradient(
            45deg,
            transparent,
            transparent 35px,
            rgba(14, 61, 89, 0.03) 35px,
            rgba(14, 61, 89, 0.03) 70px
          );
        }
      `}</style>

      <div className="min-h-screen bg-[#F9FDFE]">
        {/* Header */}
        <header
          className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-[#F9FDFE] shadow-lg' : 'bg-transparent'
            }`}
          style={{ height: '80px' }}
        >
          <div className="container mx-auto px-6 h-full flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#0E3D59] rounded flex items-center justify-center">
                <Shield className="w-6 h-6 text-[#F9FDFE]" />
              </div>
              <span className="text-xl md:text-2xl text-[#051726] logo-font">
                SWIFT LEAD DIGITAL
              </span>
            </div>

            {/* Navigation */}
            <nav className="hidden md:flex items-center gap-8 text-[#051726] font-bold text-sm uppercase tracking-wider">
              <a href="#features" className="hover:text-[#A67655] transition-colors">Features</a>
              <a href="#pricing" className="hover:text-[#A67655] transition-colors">Pricing</a>
              <a href="#company" className="hover:text-[#A67655] transition-colors">Company</a>
            </nav>

            {/* CTA Buttons */}
            <div className="flex items-center gap-4">
              <button className="hidden md:block px-6 py-2 border-2 border-[#A67655] text-[#A67655] rounded-lg font-bold hover:bg-[#A67655] hover:text-[#F9FDFE] transition-all">
                LOGIN
              </button>
              <a
                href="https://swiftleaddigital.com/grow-business"
                className="px-6 py-2 bg-[#A67655] text-[#F9FDFE] rounded-lg font-bold hover:scale-105 transition-all"
              >
                APPLY
              </a>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section id="hero" className="min-h-screen flex items-center pt-20 gradient-bg">
          <div className="container mx-auto px-6 py-24">
            <div className="max-w-6xl mx-auto">
              {/* Flagging Headline */}
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#051726] text-center mb-12 uppercase">
                ATTENTION PERSONAL INJURY ATTORNEYS: ARE SKYROCKETING AD COSTS AND JUNK LEADS BLEEDING YOUR FIRM DRY?
              </h1>

              <div className="grid md:grid-cols-2 gap-12">
                {/* Left Column - Pain Agitation */}
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-[#A6A5A4]">
                    The Brutal Truth: You're Losing $400–$1,500 Per Signed Case to Worthless Clicks.
                  </h2>
                  <p className="text-lg text-[#A6A5A4] leading-relaxed">
                    Imagine this nightmare: Another month ends with your ad spend exploding past $50K, but your caseload is clogged with lowball slip-and-falls worth peanuts—while high-value 18-wheeler crashes slip away to competitors. You're grinding 80-hour weeks, watching profits evaporate as junk leads pile up, forcing you to turn away real cases or settle for scraps. How long can you survive this bloodbath? Clients suffer, your reputation erodes, and retirement dreams fade—all because blind bidding leaves you guessing in the dark. It's not just money; it's your firm's future on the line. Agree?
                  </p>
                </div>

                {/* Right Column - Solution & Engagement */}
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-[#A67655] uppercase">
                    STOP THE GUESSWORK.
                  </h2>
                  <p className="text-lg text-[#A6A5A4]">
                    There's a smarter, simpler way. Swift Lead Digital ends the guesswork, turning wasted clicks into high-value cases. Curious how it works?
                  </p>
                  <p className="text-lg text-[#A6A5A4] leading-relaxed">
                    What if you could slash those wasteful bids and lock in $30K+ fee cases like the pros—optimizing every click for long-term scale, all while staying ethically bulletproof under Texas Bar rules? Imagine ditching the guesswork, boosting ROI 30–40% potential, and watching competitors eat your dust. But how? This isn't some tactic—it's the brutal edge that turns pain into profit. Ready to see what's possible without screwing around?
                  </p>

                  {/* Bullet Points */}
                  <ul className="space-y-3 text-[#A6A5A4]">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-[#A67655] mt-1 flex-shrink-0" />
                      <span>Filter junk automatically—why let low-value leads keep bleeding you?</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-[#A67655] mt-1 flex-shrink-0" />
                      <span>Prioritize payout monsters—feel what it's like to scale like elite firms?</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-[#A67655] mt-1 flex-shrink-0" />
                      <span>Compliant, transparent edge—beat rivals without the risks?</span>
                    </li>
                  </ul>

                  {/* CTA Button */}
                  <button className="cta-button w-full md:w-auto">
                    BOOK YOUR DEMO NOW
                  </button>

                  {/* Disclaimer */}
                  <p className="text-xs text-[#A6A5A4] italic text-center">
                    Results vary; not guaranteed. Consult ethics counsel per TDRPC 5.04/7.03. Claims from benchmarks/potential outcomes.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why It's Needed Section */}
        <section id="why-needed" className="py-24 stripe-bg">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-[#051726] mb-12 text-center uppercase">
                WHY PERSONAL INJURY FIRMS NEED AI BIDDING ASSISTANCE NOW
              </h2>

              <div className="grid md:grid-cols-2 gap-12">
                {/* Left Column */}
                <div className="space-y-6">
                  <p className="text-lg text-[#A6A5A4]">
                    Personal injury attorneys face skyrocketing ad costs and low-value leads diluting profits.
                  </p>
                  <p className="text-lg text-[#A6A5A4]">
                    Our AI saves $400–$1,500 per case by filtering junk and prioritizing high-payout opportunities (e.g., from $30K to $39K average fees).
                  </p>
                  <p className="text-lg text-[#A6A5A4]">
                    Our software can potentially boost average contingency fees by 30-40% by focusing on high-value cases (like 18-wheeler accidents) vs. low value ones; results may vary.
                  </p>

                  {/* Bullet List */}
                  <ul className="space-y-4 text-[#A6A5A4]">
                    <li className="flex items-start gap-3">
                      <Target className="w-6 h-6 text-[#0E3D59] mt-1 flex-shrink-0" />
                      <span>Stop funding junk leads before intake wastes your time and money.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <TrendingUp className="w-6 h-6 text-[#0E3D59] mt-1 flex-shrink-0" />
                      <span>Dominate premium cases by auto prioritizing the claims others miss.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Zap className="w-6 h-6 text-[#0E3D59] mt-1 flex-shrink-0" />
                      <span>Scale with certainty as AI reallocates budget instantly when market costs shift.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Shield className="w-6 h-6 text-[#0E3D59] mt-1 flex-shrink-0" />
                      <span>Stay ethics safe with full compliance to ABA and state ad rules.</span>
                    </li>
                  </ul>
                </div>

                {/* Right Column - Data Table */}
                <div className="bg-white border border-[#A6A5A4]/20 rounded-lg shadow-lg overflow-hidden">
                  <table className="w-full data-font">
                    <thead className="bg-[#051726] text-[#F9FDFE]">
                      <tr>
                        <th className="px-6 py-4 text-left">Metric</th>
                        <th className="px-6 py-4 text-right">Before AI</th>
                        <th className="px-6 py-4 text-right">With AI</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-[#E5F0F5] hover:bg-[#E5F0F5] transition-colors">
                        <td className="px-6 py-4">Cost per Case</td>
                        <td className="px-6 py-4 text-right text-red-600">$1,500</td>
                        <td className="px-6 py-4 text-right text-green-600">$850</td>
                      </tr>
                      <tr className="border-b border-[#E5F0F5] hover:bg-[#E5F0F5] transition-colors">
                        <td className="px-6 py-4">Avg Case Value</td>
                        <td className="px-6 py-4 text-right">$30K</td>
                        <td className="px-6 py-4 text-right text-green-600">$39K</td>
                      </tr>
                      <tr className="border-b border-[#E5F0F5] hover:bg-[#E5F0F5] transition-colors">
                        <td className="px-6 py-4">Junk Lead %</td>
                        <td className="px-6 py-4 text-right text-red-600">40%</td>
                        <td className="px-6 py-4 text-right text-green-600">12%</td>
                      </tr>
                      <tr className="hover:bg-[#E5F0F5] transition-colors">
                        <td className="px-6 py-4">ROAS</td>
                        <td className="px-6 py-4 text-right">4.2x</td>
                        <td className="px-6 py-4 text-right text-green-600">7.3x</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* CTA */}
              <div className="mt-12 text-center">
                <p className="text-xl text-[#051726] mb-6">
                  Ready to beat the competition without the guesswork?
                </p>
                <button className="cta-button">
                  GET STARTED
                </button>
              </div>

              {/* Ethics Footer */}
              <p className="text-xs text-[#A6A5A4] italic text-center mt-8">
                Results vary by firm size, mix, and market; not a promise of income. Attorneys must ensure all ads comply with ABA Model Rules 7.1–7.5 and state laws (e.g., Texas TDRPC 5.04, 7.03). Consult your state's ethics counsel before use, per applicable bar rules (e.g., TDRPC 5.04/7.03 in Texas).
              </p>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section id="how-it-works" className="py-24 bg-[#E5F0F5]/30">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-[#051726] mb-4 text-center uppercase">
                NO GUESSWORK. JUST RESULTS.
              </h2>
              <p className="text-xl text-[#A6A5A4] text-center mb-12">
                Slash acquisition costs. Win premium traffic. Stay compliant.
              </p>

              {/* Blind Bidding vs AI Comparison */}
              <div className="bg-white rounded-lg shadow-xl overflow-hidden mb-12">
                <table className="w-full data-font">
                  <thead>
                    <tr>
                      <th className="bg-[#A67655] text-[#F9FDFE] px-6 py-4 text-left">Blind Bidding</th>
                      <th className="bg-[#0E3D59] text-[#F9FDFE] px-6 py-4 text-left">AI Bidding Assistance</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-[#E5F0F5]">
                      <td className="px-6 py-6 text-[#A6A5A4] align-top">
                        Your firm guesses at how much should be spent on advertising
                      </td>
                      <td className="px-6 py-6 text-[#051726] align-top">
                        <div className="flex items-start gap-3">
                          <Activity className="w-6 h-6 text-[#0E3D59] mt-1 flex-shrink-0" />
                          <span>Our plugin automatically collects anonymized firm data without PII in order to refine your ad spend.</span>
                        </div>
                      </td>
                    </tr>
                    <tr className="border-b border-[#E5F0F5]">
                      <td className="px-6 py-6 text-[#A6A5A4] align-top">
                        Fog of guesswork drains thousands monthly by overspending on ads
                      </td>
                      <td className="px-6 py-6 text-[#051726] align-top">
                        <div className="flex items-start gap-3">
                          <BarChart3 className="w-6 h-6 text-[#0E3D59] mt-1 flex-shrink-0" />
                          <span>AI calculates estimated click value by ZIP + crash type. More precise estimates of the value of a case allow your firm to digitally compete for more valuable cases.</span>
                        </div>
                      </td>
                    </tr>
                    <tr className="border-b border-[#E5F0F5]">
                      <td className="px-6 py-6 text-[#A6A5A4] align-top">
                        Blind bidding leads to junk leads flood intake, profits hemorrhage. High-payout cases lost to rivals forever
                      </td>
                      <td className="px-6 py-6 text-[#051726] align-top">
                        <div className="flex items-start gap-3">
                          <Target className="w-6 h-6 text-[#0E3D59] mt-1 flex-shrink-0" />
                          <span>We automatically raise bids on premium cases, drop junk bids</span>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td className="px-6 py-6 text-[#A6A5A4] align-top">
                        Default Google and Meta reporting hides true ad cost per settled case. Blind spots in reporting allow wasted effort to stack up
                      </td>
                      <td className="px-6 py-6 text-[#051726] align-top">
                        <div className="flex items-start gap-3">
                          <FileText className="w-6 h-6 text-[#0E3D59] mt-1 flex-shrink-0" />
                          <span>Dashboard updates every hour with metrics that show what a good lead is worth to your firm</span>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Trust Signals */}
              <div className="bg-[#0E3D59]/10 border-2 border-[#0E3D59] rounded-lg p-6 mb-12">
                <div className="flex items-center gap-3 text-[#051726]">
                  <Lock className="w-8 h-8 text-[#0E3D59]" />
                  <span className="font-bold">Your data remains anonymized and hashed—no PII</span>
                </div>
              </div>

              <p className="text-xl text-[#051726] text-center mb-6 font-bold uppercase">
                SEE WHY TOP FIRMS WIN WITH AI. JOIN THEM.
              </p>
              <div className="text-center">
                <button className="cta-button">
                  GET STARTED
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Compliance Section */}
        <section id="compliance" className="py-24 bg-white">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-[#051726] mb-12 text-center uppercase">
                LEGAL COMPLIANCE AND ETHICS EVERY STEP OF THE WAY
              </h2>

              <div className="grid md:grid-cols-3 gap-8 mb-12">
                <div className="bg-[#F9FDFE] border border-[#A6A5A4]/20 rounded-lg p-6">
                  <Shield className="w-12 h-12 text-[#0E3D59] mb-4" />
                  <p className="text-[#051726]">
                    Clients must consent to anonymized data sharing via contract clause—no disclosures without permission.
                  </p>
                </div>
                <div className="bg-[#F9FDFE] border border-[#A6A5A4]/20 rounded-lg p-6">
                  <FileText className="w-12 h-12 text-[#0E3D59] mb-4" />
                  <p className="text-[#051726]">
                    Monthly reporting gives your law full visibility into the anonymized data that is collected.
                  </p>
                </div>
                <div className="bg-[#F9FDFE] border border-[#A6A5A4]/20 rounded-lg p-6">
                  <Lock className="w-12 h-12 text-[#0E3D59] mb-4" />
                  <p className="text-[#051726]">
                    Processing of firm data occurs before anything is sent to Swift Lead Digital. Every effort possible is made to ensure that attorney-client privilege is never breached.
                  </p>
                </div>
              </div>

              {/* What We Do */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold text-[#051726] mb-6 uppercase">
                  WHAT WE DO
                </h3>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-[#0E3D59] mt-1 flex-shrink-0" />
                    <span className="text-[#A6A5A4]">Collaborate on web intake forms to ensure essential click data is captured from Google and Meta</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-[#0E3D59] mt-1 flex-shrink-0" />
                    <span className="text-[#A6A5A4]">Generate estimated contingency fee values based on public and firm data</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-[#0E3D59] mt-1 flex-shrink-0" />
                    <span className="text-[#A6A5A4]">Deliver ongoing updates to Google and Meta (Builds on your data for sustained edge)</span>
                  </li>
                </ul>
              </div>

              <p className="text-xl text-[#051726] text-center mb-6">
                The path to predictable, high-value cases is just one click away. Are you ready?
              </p>
              <div className="text-center">
                <button className="cta-button">
                  GET STARTED
                </button>
              </div>

              {/* What Your Firm Does */}
              <div className="mt-12">
                <h3 className="text-2xl font-bold text-[#051726] mb-6 uppercase">
                  WHAT YOUR FIRM DOES
                </h3>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <ArrowRight className="w-6 h-6 text-[#0E3D59] mt-1 flex-shrink-0" />
                    <span className="text-[#A6A5A4]">Add a simple clause to your standard contracts for anonymized data sharing</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ArrowRight className="w-6 h-6 text-[#0E3D59] mt-1 flex-shrink-0" />
                    <span className="text-[#A6A5A4]">Grant limited API access to ad accounts. Your firm retains full control of creatives and billing</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ArrowRight className="w-6 h-6 text-[#0E3D59] mt-1 flex-shrink-0" />
                    <span className="text-[#A6A5A4]">Install our plugin for Clio/CMS to capture hashed IDs, case codes, zip codes, and fees (only if client consents and anonymized). Efficient tip: Takes under an hour, unlocks long-term ROI</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Dashboard Section */}
        <section id="dashboard" className="py-24 gradient-bg">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-[#051726] mb-6 text-center uppercase">
                Reporting that gives you full visibility
              </h2>
              <p className="text-xl text-[#A6A5A4] text-center mb-12">
                The moment you plug in, the dashboard shows exactly which clicks drain profit and which ones predict $30K+ settlements. No guesswork. No excuses. Live. Hourly.
              </p>

              {/* Mock Dashboard */}
              <div className="bg-white rounded-lg shadow-2xl p-8 mb-12">
                <div className="grid md:grid-cols-3 gap-6">
                  {/* Real Time ROAS */}
                  <div className="bg-[#F9FDFE] border border-[#A6A5A4]/20 rounded-lg p-6">
                    <h3 className="text-lg font-bold text-[#051726] mb-2 uppercase">Real Time ROAS</h3>
                    <div className="text-3xl font-bold text-[#0E3D59] data-font">7.3x</div>
                    <div className="flex items-center gap-2 mt-2">
                      <TrendingUp className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-green-600">+81% from baseline</span>
                    </div>
                  </div>

                  {/* Spend Redirected */}
                  <div className="bg-[#F9FDFE] border border-[#A6A5A4]/20 rounded-lg p-6">
                    <h3 className="text-lg font-bold text-[#051726] mb-2 uppercase">Spend Redirected to 18 Wheeler Leads</h3>
                    <div className="text-3xl font-bold text-[#0E3D59] data-font">$45K</div>
                    <div className="bg-[#E5F0F5] rounded-full h-2 mt-3">
                      <div className="bg-[#0E3D59] h-2 rounded-full" style={{ width: '73%' }}></div>
                    </div>
                  </div>

                  {/* Compliance Log */}
                  <div className="bg-[#F9FDFE] border border-[#A6A5A4]/20 rounded-lg p-6">
                    <h3 className="text-lg font-bold text-[#051726] mb-2 uppercase">Compliance Log</h3>
                    <div className="bg-[#051726] text-[#00FF00] p-3 rounded font-mono text-xs">
                      <div>HASH: a7f2b9...</div>
                      <div>HASH: c3d8e1...</div>
                      <div>HASH: 9b2f4a...</div>
                    </div>
                  </div>
                </div>

                {/* Additional Metrics */}
                <div className="grid md:grid-cols-2 gap-6 mt-6">
                  <div className="bg-[#F9FDFE] border border-[#A6A5A4]/20 rounded-lg p-6">
                    <h3 className="text-lg font-bold text-[#051726] mb-2 uppercase">Cost Per Signed Case</h3>
                    <div className="text-3xl font-bold text-[#0E3D59] data-font">$850</div>
                    <div className="flex items-center gap-2 mt-2">
                      <TrendingDown className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-green-600">Down from $1,500</span>
                    </div>
                  </div>

                  <div className="bg-[#F9FDFE] border border-[#A6A5A4]/20 rounded-lg p-6">
                    <h3 className="text-lg font-bold text-[#051726] mb-2 uppercase">Average Case Value</h3>
                    <div className="text-3xl font-bold text-[#0E3D59] data-font">$39K</div>
                    <div className="flex items-center gap-2 mt-2">
                      <TrendingUp className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-green-600">Up from $30K baseline</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="text-center space-y-6">
                <div className="bg-[#E5F0F5] border-2 border-[#0E3D59] rounded-lg p-6 max-w-3xl mx-auto">
                  <h3 className="text-xl font-bold text-[#051726] mb-3 uppercase">Big Benefit</h3>
                  <p className="text-[#A6A5A4]">
                    Stop guessing, start governing. Attorneys with this view routinely trim 25% spend without losing a single premium collision intake.
                  </p>
                </div>

                <div className="bg-white border border-[#A6A5A4]/20 rounded-lg p-6 max-w-3xl mx-auto">
                  <div className="flex items-start gap-3">
                    <Lock className="w-8 h-8 text-[#0E3D59] mt-1 flex-shrink-0" />
                    <div className="text-left">
                      <h4 className="font-bold text-[#051726] mb-2">Compliance Baked In</h4>
                      <p className="text-[#A6A5A4] text-sm">
                        All data runs through hashed, anonymized pipelines—no names, no health info. Maintain full control of messaging, meeting ABA Model Rule 7.2 and Texas TDRPC 7.03, with no fee-sharing risk under Rule 5.04.
                      </p>
                    </div>
                  </div>
                </div>

                <p className="text-[#051726] font-bold">
                  Every dashboard metric ties cleanly to net fees recovered per signed case. Instead of wasting ad spend, you now have a tool to help you strategically conquer your market.
                </p>

                <p className="text-xl text-[#051726] font-bold uppercase">
                  STOP THE PAIN OF LOST PROFITS. TAKE CONTROL NOW.
                </p>

                <button className="cta-button">
                  GET MY DASHBOARD
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-[#051726] text-[#F9FDFE] py-12">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <div className="grid md:grid-cols-3 gap-8">
                {/* Logo and Copyright */}
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-[#0E3D59] rounded flex items-center justify-center">
                      <Shield className="w-6 h-6 text-[#F9FDFE]" />
                    </div>
                    <span className="text-xl logo-font">SWIFT LEAD DIGITAL</span>
                  </div>
                  <p className="text-sm text-[#A6A5A4]">© 2025 Swift Lead Digital. All rights reserved.</p>
                </div>

                {/* Navigation Links */}
                <div>
                  <h4 className="font-bold mb-4 uppercase">Quick Links</h4>
                  <ul className="space-y-2 text-sm">
                    <li><a href="#features" className="text-[#A6A5A4] hover:text-[#F9FDFE] transition-colors">Features</a></li>
                    <li><a href="#pricing" className="text-[#A6A5A4] hover:text-[#F9FDFE] transition-colors">Pricing</a></li>
                    <li><a href="#company" className="text-[#A6A5A4] hover:text-[#F9FDFE] transition-colors">Company</a></li>
                  </ul>
                </div>

                {/* Contact Information */}
                <div>
                  <h4 className="font-bold mb-4 uppercase">Contact</h4>
                  <p className="text-sm text-[#A6A5A4] mb-2">
                    If you have any questions or comments please reach out to:
                  </p>
                  <p className="text-sm">
                    <strong>Ralph Brooks</strong><br />
                    Swift Lead Digital<br />
                    Email: ralph.brooks@swiftleaddigital.com
                  </p>
                </div>
              </div>

              {/* Disclaimer */}
              <div className="mt-8 pt-8 border-t border-[#0E3D59]">
                <p className="text-xs text-[#A6A5A4] text-center italic">
                  Results vary; consult ethics counsel per TDRPC 5.04/7.03. Claims based on potential outcomes, not guaranteed. Per TDRPC Rules 5.04/7.03.
                </p>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default App;